import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import Layout from "../components/Layout";
import { Card } from "../components/ui/card";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell,
} from "recharts";
import { Users2, TrendingUp, ClipboardList, AlertTriangle, MailPlus } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { formatApiError } from "../lib/api";

const STAGE_COLORS = ["#3B82F6", "#F59E0B", "#8B5CF6", "#10B981", "#F97316", "#14B8A6"];

function Stat({ icon: Icon, label, value, tint, testid }) {
  return (
    <Card className="p-5 border border-slate-200 shadow-none" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <div className="eyebrow">{label}</div>
          <div className="mt-2 text-3xl font-display font-semibold text-[#0B192C] tracking-tight">
            {value}
          </div>
        </div>
        <div className="w-10 h-10 rounded-md flex items-center justify-center"
             style={{ background: tint || "#F3F4F6" }}>
          <Icon size={18} strokeWidth={1.6} className="text-[#7A1B28]" />
        </div>
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const { t, ts, tc } = useI18n();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [sendingReminders, setSendingReminders] = useState(false);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/students").then((r) => setRecent(r.data.slice(0, 5))).catch(() => {});
  }, []);

  const sendReminders = async () => {
    setSendingReminders(true);
    try {
      const { data } = await api.post("/tasks/send-reminders");
      toast.success(`${t("reminderSent")}: ${data.sent}/${data.users}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSendingReminders(false);
    }
  };

  const countryData = stats
    ? Object.entries(stats.per_country)
        .map(([name, count]) => ({ name, label: tc(name), count }))
        .sort((a, b) => b.count - a.count)
    : [];
  const stageData = stats
    ? Object.entries(stats.per_stage).map(([name, count]) => ({ name: ts(name), count }))
    : [];

  return (
    <Layout>
      <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-[1400px] mx-auto">
        <div className="mb-8 flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="eyebrow">{t("tagline")}</div>
            <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-[#0B192C] mt-1">
              {t("dashboard")}
            </h1>
          </div>
          <Button variant="outline"
                  onClick={sendReminders}
                  disabled={sendingReminders}
                  className="border-slate-200"
                  data-testid="send-reminders-btn">
            <MailPlus size={16} className="me-2" /> {t("sendReminders")}
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Stat icon={Users2} label={t("totalStudents")} value={stats?.total_students ?? "—"}
                testid="stat-total" />
          <Stat icon={TrendingUp} label={t("conversionRate")}
                value={stats ? `${stats.conversion_rate}%` : "—"} testid="stat-conversion" />
          <Stat icon={ClipboardList} label={t("openTasks")} value={stats?.open_tasks ?? "—"}
                testid="stat-open-tasks" />
          <Stat icon={AlertTriangle} label={t("overdueTasks")} value={stats?.overdue_tasks ?? "—"}
                testid="stat-overdue" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2 p-6 border border-slate-200 shadow-none" data-testid="chart-funnel">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-semibold text-[#0B192C]">{t("pipelineFunnel")}</h3>
              <div className="text-xs text-slate-500">{t("stages")}</div>
            </div>
            <div style={{ width: "100%", height: 300, minHeight: 300, minWidth: 0 }}>
              <ResponsiveContainer>
                <BarChart data={stageData} margin={{ top: 10, right: 10, bottom: 10, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#6B7280" }}
                         interval={0} angle={-15} textAnchor="end" height={60} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#6B7280" }} />
                  <Tooltip cursor={{ fill: "rgba(122,27,40,0.05)" }} />
                  <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                    {stageData.map((_, i) => (
                      <Cell key={i} fill={STAGE_COLORS[i % STAGE_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card className="p-6 border border-slate-200 shadow-none" data-testid="chart-countries">
            <h3 className="font-display text-lg font-semibold text-[#0B192C] mb-4">
              {t("studentsPerCountry")}
            </h3>
            <div className="space-y-2">
              {countryData.slice(0, 8).map((c) => (
                <div key={c.name} className="flex items-center gap-2">
                  <div className="w-24 text-sm text-slate-600 truncate">{c.label}</div>
                  <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full"
                         style={{
                           width: `${Math.min(100, (c.count / Math.max(1, countryData[0]?.count || 1)) * 100)}%`,
                           background: "linear-gradient(90deg,#0B192C,#7A1B28)",
                         }} />
                  </div>
                  <div className="w-8 text-end text-sm font-medium text-[#0B192C]">{c.count}</div>
                </div>
              ))}
              {countryData.every((c) => c.count === 0) && (
                <div className="text-sm text-slate-500">{t("empty")}</div>
              )}
            </div>
          </Card>
        </div>

        <Card className="mt-6 p-6 border border-slate-200 shadow-none" data-testid="recent-students">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg font-semibold text-[#0B192C]">{t("recentStudents")}</h3>
            <Link to="/students" className="text-sm text-[#7A1B28] hover:underline">
              {t("students")} →
            </Link>
          </div>
          {recent.length === 0 ? (
            <div className="text-sm text-slate-500">{t("noStudents")}</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {recent.map((s) => (
                <Link key={s.id} to={`/students/${s.id}`}
                      className="flex items-center justify-between py-3 hover:bg-slate-50 rounded-md px-2 -mx-2">
                  <div>
                    <div className="font-medium text-[#0B192C]">{s.name}</div>
                    <div className="text-xs text-slate-500">{s.nationality || "—"} · {s.education_level || "—"}</div>
                  </div>
                  <div className="text-xs px-2 py-1 rounded-md bg-slate-100 text-slate-700">{ts(s.stage)}</div>
                </Link>
              ))}
            </div>
          )}
        </Card>
      </div>
    </Layout>
  );
}
